import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CreditCard, ShieldCheck, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { trackEvent, attachCampaign } from "@/lib/analytics";
import { PayPalSkuButton } from "@/components/PayPalSkuButton";
import { BookingDetailsModal } from "@/components/BookingDetailsModal";
import { categoryRequiresClassSelection } from "@/data/bookingOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  kids: "USDD Kids",
  adult: "Adults",
  workshop: "Workshops",
  online: "Online · USDD+",
  qualification: "Coach Qualification",
  event: "Events",
};

export const Pricing = ({ limit }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySku, setBusySku] = useState(null);

  // Modal state: which product + which provider triggered the flow
  const [modalProduct, setModalProduct] = useState(null);
  const [modalProvider, setModalProvider] = useState(null); // "stripe" | "paypal"

  // Per-SKU booking details captured before checkout. Indexed by sku.
  // PayPal Smart Buttons fire createOrder out-of-band, so we read this ref
  // synchronously rather than going through React state.
  const pendingBookingRef = useRef({});
  // PayPal "armed" SKU — only this SKU's Smart Button is allowed to fire
  // createOrder until the user gives details again.
  const [paypalArmedSku, setPaypalArmedSku] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/products`).then((r) => {
      if (cancel) return;
      setProducts(limit ? r.data.slice(0, limit) : r.data);
    }).catch(() => toast.error("Couldn't load pricing."))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [limit]);

  const openModal = (product, provider) => {
    setModalProduct(product);
    setModalProvider(provider);
  };

  const closeModal = () => {
    setModalProduct(null);
    setModalProvider(null);
  };

  // Called when user confirms details inside the modal.
  // - For Stripe: create checkout session, redirect.
  // - For PayPal: stash booking in ref + arm the SKU, then close modal so
  //   the PayPal SDK button next to the card visibly triggers the popup.
  const onConfirmDetails = async (booking) => {
    if (!modalProduct || !modalProvider) return;
    const bookingWithUtm = attachCampaign(booking);
    if (modalProvider === "stripe") {
      try {
        setBusySku(modalProduct.sku);
        trackEvent("begin_checkout", {
          sku: modalProduct.sku,
          method: "stripe",
          value: modalProduct.price,
          currency: "GBP",
          content_ids: [modalProduct.sku],
          content_name: modalProduct.title,
          content_type: "product",
        });
        const r = await axios.post(`${API}/checkout/stripe`, {
          sku: modalProduct.sku,
          quantity: 1,
          origin_url: window.location.origin,
          customer_email: booking.email,
          booking: bookingWithUtm,
        });
        if (r.data?.url) {
          closeModal();
          window.location.href = r.data.url;
          return;
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Checkout failed.");
        throw new Error(e?.response?.data?.detail || "Checkout failed.");
      } finally {
        setBusySku(null);
      }
    }
    if (modalProvider === "paypal") {
      pendingBookingRef.current[modalProduct.sku] = bookingWithUtm;
      setPaypalArmedSku(modalProduct.sku);
      trackEvent("begin_checkout", {
        sku: modalProduct.sku,
        method: "paypal",
        value: modalProduct.price,
        currency: "GBP",
        content_ids: [modalProduct.sku],
        content_name: modalProduct.title,
        content_type: "product",
      });
      closeModal();
      toast.success("Details saved — pick PayPal below to complete payment.", { duration: 4000 });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-zinc-400 py-12">
        <Loader2 size={18} className="animate-spin" /> Loading pricing…
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p, i) => {
          const armed = paypalArmedSku === p.sku;
          const requiresDetails = categoryRequiresClassSelection(p.category);
          return (
            <motion.div
              key={p.sku}
              data-testid={`pricing-card-${p.sku}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className={`relative p-6 rounded-2xl border transition-all flex flex-col ${
                p.featured
                  ? "bg-gradient-to-br from-brand-purpleDeep/40 to-zinc-950 border-brand-yellow/40"
                  : "bg-white/[0.03] border-white/10 hover:border-brand-yellow/30"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-5 text-[10px] font-black uppercase tracking-[0.25em] bg-brand-yellow text-black px-3 py-1 rounded-full glow-yellow">
                  Popular
                </span>
              )}
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">
                {CATEGORY_LABEL[p.category] || p.category}
              </div>
              <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{p.title}</h3>
              <p className="mt-2 text-xs text-zinc-400 leading-relaxed flex-1">{p.description}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-4xl text-white tracking-wide">
                  £{Number(p.price).toFixed(p.price % 1 ? 2 : 0)}
                </span>
                {p.interval && (
                  <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">/ {p.interval}</span>
                )}
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <button
                  onClick={() => openModal(p, "stripe")}
                  disabled={busySku === p.sku}
                  data-testid={`buy-stripe-${p.sku}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow disabled:opacity-60"
                >
                  {busySku === p.sku ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                  Pay with Card
                </button>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 my-1">
                  <span className="flex-1 h-px bg-white/10" />
                  or
                  <span className="flex-1 h-px bg-white/10" />
                </div>

                {armed ? (
                  <PayPalSkuButton
                    sku={p.sku}
                    getBooking={() => pendingBookingRef.current[p.sku]}
                    onCompleted={() => setPaypalArmedSku(null)}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => openModal(p, "paypal")}
                    data-testid={`buy-paypal-${p.sku}`}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-xs tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
                  >
                    PayPal
                  </button>
                )}

                <Link
                  to="/schedule"
                  data-testid={`book-schedule-${p.sku}`}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-zinc-400 hover:text-brand-yellow text-[10px] uppercase tracking-[0.25em] font-bold transition-all"
                >
                  <Calendar size={11} /> Or browse the class schedule
                </Link>
              </div>

              <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <ShieldCheck size={11} /> {requiresDetails ? "Class details captured before checkout" : "Secure checkout"}
              </div>
            </motion.div>
          );
        })}
      </div>

      <BookingDetailsModal
        open={!!modalProduct}
        onClose={closeModal}
        product={modalProduct}
        onConfirm={onConfirmDetails}
        submitLabel={modalProvider === "paypal" ? "Continue to PayPal" : "Continue to Card"}
      />
    </>
  );
};
